/**
 * L'équipe du chantier : qui répond de quoi.
 *
 * *Métier, 13/08/2026 — d'abord : « renseigner sur la contre-étude le nom du
 * conducteur, des chefs de chantier, chefs d'équipe […] ou bien, s'il n'y a pas ces
 * fonctions impliquées dans le chantier, le nom d'un responsable qui est forcément dans
 * la table salarié », avec « plusieurs personnes par fonction ». Puis, en corrigeant ma
 * première version : « uniquement Conducteur, Chef d'équipe, Chef de chantier et la
 * possibilité de rajouter un nom de responsable s'il n'y a personne dans les fonctions
 * précédentes ».*
 *
 * ## Ce que la première version faisait de faux
 *
 * Elle déduisait les fonctions des **codes de main-d'œuvre chiffrés** dans la
 * contre-étude. Deux défauts, constatés sur un chantier réel : elle proposait de nommer
 * les ouvriers TP et les chauffeurs — ce n'est pas ce qu'on nomme ici, c'est le travail
 * du lot 2 —, et elle **ratait le conducteur de travaux**, qui n'apparaît dans aucun
 * déboursé puisque ses heures sont des frais généraux.
 *
 * Ce qu'on nomme sur une contre-étude est l'**encadrement**. Ça ne se déduit pas d'un
 * chiffrage, ça se décide — d'où la colonne « à nommer » du référentiel (migration 018),
 * cochée par le métier, jamais devinée par le code.
 *
 * ## Le responsable est un REPLI, pas une quatrième fonction
 *
 * Il n'apparaît que si **personne** n'est nommé dans les fonctions d'encadrement — les
 * mots du métier : « la possibilité de rajouter un nom de responsable s'il n'y a
 * personne dans les fonctions précédentes ». Un chantier qui a son chef de chantier n'a
 * pas besoin d'un responsable, et l'offrir quand même laisserait s'installer des équipes
 * à deux têtes dont aucune ne fait autorité.
 *
 * Il reste visible tant qu'il est pourvu, sinon on ne pourrait plus le retirer.
 *
 * ## L'équipe appartient au CHANTIER
 *
 * *Tranché avec le métier le 13/08/2026.* Elle s'affiche donc identique quelle que soit
 * la version regardée, et **reste modifiable sur une version figée** : figer un
 * chiffrage ne fige pas les gens.
 */
import { useCallback, useEffect, useState } from "react";
import type { JSX } from "react";
import type { Generique } from "@planif/moteur";
import {
  affecter,
  chargerAffectations,
  chargerFonctionsANommer,
  chargerSalaries,
  retirerAffectation,
} from "./api";
import type { Affectation, FonctionANommer, Salarie } from "./api";

/** Le rôle « responsable » : un code nul, une clé de liste qui n'est pas nulle. */
const RESPONSABLE = "__responsable__";

type Role = {
  /** `null` pour le responsable. */
  readonly codeMo: string | null;
  readonly libelle: string;
  /**
   * Le rôle précis (056). `null` = le rôle unique du code, ou le responsable.
   *
   * Depuis que deux rôles peuvent partager un code — l'assistante administrative et l'assistante
   * référente sur `_MOIADMTRX` —, c'est LUI qui distingue deux lignes du tableau, pas le code.
   */
  readonly fonctionId: string | null;
  /** Vrai quand un autre rôle partage ce code : le vivier de candidats est le même. */
  readonly partageLeCode: boolean;
};

/** « Jean MARTIN — Chef de chantier », ou juste le nom si le référentiel n'a pas le poste. */
function decrire(personne: Affectation | Salarie): string {
  const nom = `${personne.prenom} ${personne.nom}`;
  return personne.poste === null ? nom : `${nom} — ${personne.poste}`;
}

export function Equipe({
  chantierId,
  generiques,
}: {
  chantierId: string;
  /** Le référentiel MO : ce sont ses fonctions cochées « à nommer » qui font les lignes. */
  generiques: readonly Generique[];
}): JSX.Element {
  const [affectations, setAffectations] = useState<readonly Affectation[]>([]);
  const [salaries, setSalaries] = useState<readonly Salarie[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [alerteReferentiel, setAlerteReferentiel] = useState<string | null>(null);
  const [enAttente, setEnAttente] = useState(false);
  const [deplie, setDeplie] = useState(false);
  /**
   * Les lignes du tableau, calculées par le SERVEUR (056).
   *
   * Elles ne se déduisent plus du référentiel MO seul : un code coché peut déclarer plusieurs
   * rôles distincts, et c'est une jointure. La calculer ici obligerait l'écran à charger la
   * table des rôles en plus des génériques, et à refaire la même jointure — deux endroits pour la
   * même réponse, ce que le produit refuse.
   */
  const [fonctionsDuServeur, setFonctionsDuServeur] = useState<readonly FonctionANommer[] | null>(
    null,
  );

  const recharger = useCallback((): void => {
    chargerAffectations(chantierId)
      .then(({ affectations: liste }) => {
        setAffectations(liste);
      })
      .catch((e: unknown) => {
        setErreur(e instanceof Error ? e.message : String(e));
      });
  }, [chantierId]);

  useEffect(recharger, [recharger]);

  // Les salariés viennent du référentiel maître, par le serveur. **Son indisponibilité
  // ne doit pas empêcher de LIRE l'équipe** : les noms affichés sont recopiés en base
  // (migration 017), seule la saisie d'une nouvelle personne a besoin de lui. D'où une
  // alerte distincte de l'erreur, et non un écran vide.
  useEffect(() => {
    let abandonne = false;
    chargerFonctionsANommer()
      .then(({ fonctions: liste }) => {
        if (!abandonne) setFonctionsDuServeur(liste);
      })
      .catch((e: unknown) => {
        if (!abandonne) setErreur(e instanceof Error ? e.message : String(e));
      });
    return () => {
      abandonne = true;
    };
  }, []);

  useEffect(() => {
    let abandonne = false;
    chargerSalaries()
      .then(({ salaries: liste }) => {
        if (!abandonne) setSalaries(liste);
      })
      .catch((e: unknown) => {
        if (!abandonne) setAlerteReferentiel(e instanceof Error ? e.message : String(e));
      });
    return () => {
      abandonne = true;
    };
  }, []);

  const agir = (promesse: Promise<unknown>): void => {
    setEnAttente(true);
    setErreur(null);
    promesse
      .then(() => {
        recharger();
      })
      .catch((e: unknown) => {
        setErreur(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setEnAttente(false);
      });
  };

  /**
   * Les fonctions d'encadrement, dans l'ordre que le serveur donne.
   *
   * Le repli sur le référentiel MO n'est pas un défaut de confort : tant que la liste des rôles
   * n'est pas arrivée, l'écran affiche **les mêmes lignes qu'avant la 056** plutôt que rien. Un
   * tableau vide se lirait « aucune fonction n'est cochée », c'est-à-dire un message faux.
   */
  const fonctions: readonly Role[] =
    fonctionsDuServeur !== null
      ? fonctionsDuServeur.map((f) => ({
          codeMo: f.codeMo,
          libelle: f.libelle,
          fonctionId: f.id,
          partageLeCode: f.partageLeCode,
        }))
      : generiques
          .filter((g) => g.type === "MO" && g.aNommer)
          .map((g) => ({
            codeMo: g.code,
            libelle: g.libelle,
            fonctionId: null,
            partageLeCode: false,
          }));

  const nommesDansLesFonctions = affectations.filter((a) => a.codeMo !== null).length;
  const responsables = affectations.filter((a) => a.codeMo === null);

  /**
   * Le repli n'est offert que si l'encadrement est vide — et reste offert s'il est
   * pourvu, sans quoi le responsable en place deviendrait impossible à retirer.
   */
  const roles: readonly Role[] =
    nommesDansLesFonctions === 0 || responsables.length > 0
      ? [...fonctions, { codeMo: null, libelle: "Responsable du chantier", fonctionId: null, partageLeCode: false }]
      : fonctions;

  const nommes = affectations.length;

  return (
    <section className="equipe">
      {/* Le titre EST le bouton : une bande cliquable annoncée par une note grise est
          exactement l'ergonomie invisible qui a coûté deux sessions sur les
          livraisons. */}
      <button
        type="button"
        className="equipe__titre"
        onClick={() => {
          setDeplie(!deplie);
        }}
      >
        {deplie ? "▾" : "▸"} Équipe du chantier
        <span className="note">
          {nommes === 0
            ? " — personne n'est nommé"
            : ` — ${affectations.map((a) => decrire(a).split(" — ")[0]).join(", ")}`}
        </span>
      </button>

      {deplie && (
        <>
          <p className="note">
            Les noms viennent du référentiel des salariés et sont <strong>filtrés par code
            main-d'œuvre</strong> : seuls les chefs de chantier apparaissent en face de « chef
            de chantier ». Plusieurs personnes par fonction sont possibles, et{" "}
            <strong>plusieurs rôles peuvent partager un même code</strong> — le vivier est alors le
            même, la personne nommée non. Le responsable
            n'est proposé que si aucune fonction d'encadrement n'est pourvue. L'équipe
            appartient au chantier, pas à la version : elle reste la même sur tous les
            scénarios, et sur une version figée.
          </p>

          {/* Une liste vide DIT pourquoi elle est vide, et où la remplir. Sans cette
              phrase, une installation neuve verrait trois colonnes sans lignes et
              conclurait à une panne. */}
          {fonctions.length === 0 && (
            <p className="alerte">
              Aucune fonction n'est marquée « à nommer » dans le référentiel des ressources
              génériques. Les cocher dans l'écran <strong>Référentiels</strong>, colonne
              « à nommer » — conducteur de travaux, chef de chantier, chef d'équipe.
            </p>
          )}

          {alerteReferentiel !== null && (
            <p className="alerte">
              {alerteReferentiel} Les personnes déjà nommées restent lisibles, mais aucune
              nouvelle affectation n'est possible tant que le référentiel ne répond pas.
            </p>
          )}

          {erreur !== null && (
            <p className="alerte" role="alert">
              {erreur}
            </p>
          )}

          {roles.length > 0 && (
            <table className="ressources equipe__table">
              {/*
                LES LARGEURS SONT DÉCLARÉES, et c'est un correctif (signalé par Chris le
                15/09/2026 : « il faut élargir la colonne QUI car on ne voit pas la croix pour
                retirer un salarié »).

                `table.ressources` impose `table-layout: fixed` : sans `colgroup`, les quatre
                colonnes prennent **le quart chacune**, quoi qu'elles portent. La colonne « Qui »
                recevait donc 288 px pour des étiquettes qui en mesurent jusqu'à 315 — nom, prénom
                et poste du référentiel, « Stéphane CAPDEGEL — chef déquipe/cond engins » — et la
                croix de retrait débordait de 28 px sur la colonne voisine, par-dessus le menu
                déroulant. Elle restait techniquement cliquable, et visuellement introuvable.

                « Qui » est donc la seule colonne SANS largeur : elle prend tout ce que les trois
                autres n'utilisent pas. Fixer sa largeur aussi l'aurait figée au nom le plus long
                d'aujourd'hui, et le prochain recrutement aurait rouvert le même défaut.
              */}
              <colgroup>
                {/* « MOI ASSISTANTE ADMINISTRATIVE », le plus long libellé de rôle du groupe. */}
                <col style={{ width: "15rem" }} />
                {/* `_MOICDTEENG` suivi de « · vivier partagé » quand deux rôles se le partagent. */}
                <col style={{ width: "13rem" }} />
                <col />
                {/* La liste déroulante, bornée par ailleurs à 22rem. */}
                <col style={{ width: "16rem" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Fonction</th>
                  <th>Code</th>
                  <th>Qui</th>
                  <th>Ajouter</th>
                </tr>
              </thead>
              <tbody>
                {roles.map((role) => {
                  // La clé d'une ligne est le RÔLE : deux lignes peuvent partager un code.
                  const cle = role.fonctionId ?? role.codeMo ?? RESPONSABLE;
                  const tenants = affectations.filter(
                    (a) => a.codeMo === role.codeMo && a.fonctionId === role.fonctionId,
                  );
                  // Le responsable n'a pas de fonction, donc pas de filtre : c'est le
                  // rôle du chantier sans encadrement nommé, et le restreindre le
                  // viderait de son sens.
                  const candidats = (
                    role.codeMo === null
                      ? salaries
                      : salaries.filter((s) => s.codeMo === role.codeMo)
                  ).filter((s) => !tenants.some((t) => t.salarieId === s.id));

                  return (
                    <tr key={cle} className={role.codeMo === null ? "equipe__repli" : undefined}>
                      <td className="designation">{role.libelle}</td>
                      {/*
                        Le code est le FILTRE, plus l'identité du rôle (056). Quand deux rôles le
                        partagent — l'assistante administrative et l'assistante référente sur
                        `_MOIADMTRX` —, l'écran le DIT : sans cela, deux lignes au même code
                        ressembleraient à un doublon, et on hésiterait à en remplir les deux.
                      */}
                      <td className="code" title={role.partageLeCode ? `${role.codeMo ?? ""} : le même vivier de candidats sert un autre rôle de ce tableau.` : undefined}>
                        {role.codeMo ?? "—"}
                        {role.partageLeCode && <span className="note"> ·  vivier partagé</span>}
                      </td>
                      <td>
                        {tenants.length === 0 ? (
                          <span className="vide">personne</span>
                        ) : (
                          tenants.map((a) => (
                            <span key={a.id} className="personne">
                              {decrire(a)}
                              <button
                                type="button"
                                className="personne__retirer"
                                disabled={enAttente}
                                title={`Retirer ${a.prenom} ${a.nom} de ce chantier`}
                                onClick={() => {
                                  agir(retirerAffectation(chantierId, a.id));
                                }}
                              >
                                ×
                              </button>
                            </span>
                          ))
                        )}
                      </td>
                      <td>
                        {/*
                          Une liste déroulante qui affecte au choix, sans bouton de
                          validation : le geste est « je désigne quelqu'un », et il se
                          défait d'un clic sur le ×.
                        */}
                        <select
                          value=""
                          disabled={enAttente || candidats.length === 0}
                          onChange={(e) => {
                            if (e.target.value === "") return;
                            agir(affecter(chantierId, role.codeMo, e.target.value, role.fonctionId));
                          }}
                        >
                          <option value="">
                            {candidats.length === 0
                              ? salaries.length === 0
                                ? "— référentiel indisponible —"
                                : "— personne d'autre —"
                              : `— choisir parmi ${candidats.length} —`}
                          </option>
                          {candidats.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.nom} {s.prenom}
                              {s.societe === null ? "" : ` (${s.societe})`}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}
